// Trademark / legal symbols that must never reach the Adobe data layer.
const LEGAL_SYMBOLS_REGEX = /[®™©℠℗🅫🅪]/gu;

const CASE_PRESERVED_KEYS = new Set(['classes', 'clickHref']);

const sanitizeString = (value, { lowercase = true } = {}) => {
  const stripped = value.replace(LEGAL_SYMBOLS_REGEX, '').replace(/ {2,}/g, ' ').trim();
  return lowercase ? stripped.toLowerCase() : stripped;
};

const sanitizeValue = (value, key) => {
  if (typeof value === 'string') {
    return sanitizeString(value, { lowercase: !CASE_PRESERVED_KEYS.has(key) });
  }

  if (Array.isArray(value)) {
    return value.map(item => sanitizeValue(item, key));
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((acc, k) => {
      acc[k] = sanitizeValue(value[k], k);
      return acc;
    }, {});
  }

  return value;
};

// snake_case a single string: lowercase, strip legal symbols, collapse runs of
// non-alphanumeric characters to a single underscore, trim leading/trailing underscores.
const snakeCase = value =>
  String(value)
    .toLowerCase()
    .replace(LEGAL_SYMBOLS_REGEX, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const buildContext = (region, detail) => `${snakeCase(region)}_${snakeCase(detail)}`;

export const buildClickDetails = (element, translatedClickText) => {
  if (!element) {
    return {
      clickText: '',
      translatedClickText: '',
      clickHref: '',
      ariaLabel: '',
      classes: '',
    };
  }

  const rawClickText = (element.innerText || element.textContent || '').trim();
  const getAttribute = typeof element.getAttribute === 'function' ? element.getAttribute.bind(element) : () => null;
  const classes = typeof element.className === 'string' ? element.className : getAttribute('class') || '';

  const clickText = rawClickText || translatedClickText || '';
  const resolvedTranslatedClickText = translatedClickText || rawClickText || '';

  return {
    clickText,
    translatedClickText: resolvedTranslatedClickText,
    clickHref: getAttribute('href') || '',
    ariaLabel: getAttribute('aria-label') || '',
    classes,
  };
};

const pushToAdobeDataLayer = (eventName, eventData) => {
  window.adobeDataLayer = window.adobeDataLayer || [];

  const sanitizedEventData = sanitizeValue(eventData);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log(`[Analytics] ${eventName}:`, sanitizedEventData);
  }

  window.adobeDataLayer.push(sanitizedEventData);
};

export const trackCompareCheckboxClick = (shingle, color, clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'select',
      label: 'shingle comparison',
      detail: 'shingle view',
      type: 'checkbox',
      linkType: 'display action link',
      region: 'roofing shingles near me',
      regionType: 'page body',
      context: buildContext('roofing shingles near me', 'shingle view'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
    product: [
      {
        type: 'roofing shingle',
        name: `${shingle.name} ${color.name}`.toLowerCase(),
        line: shingle.name.toLowerCase(),
        detail: color.name.toLowerCase(),
      },
    ],
  };

  pushToAdobeDataLayer('trackCompareCheckboxClick', eventData);
};

export const trackAddProductClick = (clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'add product',
      linkType: 'display action link',
      type: 'button',
      region: 'shingle comparison drawer',
      regionType: 'drawer',
      context: buildContext('shingle comparison drawer', 'add product'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
  };

  pushToAdobeDataLayer('trackAddProductClick', eventData);
};

export const trackRemoveProductClick = (shingle, color, clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'remove product',
      type: 'icon',
      linkType: 'display action link',
      region: 'shingle comparison drawer',
      regionType: 'drawer',
      context: buildContext('shingle comparison drawer', 'remove product'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
    product: [
      {
        type: 'roofing shingle',
        name: `${shingle.proper_name} ${color.name}`.toLowerCase(),
        line: shingle.proper_name.toLowerCase(),
        detail: color.name.toLowerCase(),
      },
    ],
  };

  pushToAdobeDataLayer('trackRemoveProductClick', eventData);
};

export const trackCompareButtonClick = (comparisonUrl, clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'compare',
      linkType: 'internal link',
      location: comparisonUrl,
      type: 'button',
      region: 'shingle comparison drawer',
      regionType: 'drawer',
      context: buildContext('shingle comparison drawer', 'compare'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
  };

  pushToAdobeDataLayer('trackCompareButtonClick', eventData);
};

export const trackClearAllClick = (comparisonItems, clickInfo = {}) => {
  const products = comparisonItems.map(item => ({
    type: 'roofing shingle',
    name: `${item.shingle.proper_name} ${item.shingleColor.name}`.toLowerCase(),
    line: item.shingle.proper_name.toLowerCase(),
    detail: item.shingleColor.name.toLowerCase(),
  }));

  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'clear all',
      linkType: 'display action link',
      type: 'link',
      region: 'shingle comparison drawer',
      regionType: 'drawer',
      context: buildContext('shingle comparison drawer', 'clear all'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
    product: products,
  };

  pushToAdobeDataLayer('trackClearAllClick', eventData);
};

export const trackFavoriteShingleSelection = (shingleName, colorName, clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'select',
      label: 'shingle comparison',
      detail: 'compare favorite shingles',
      type: 'drop-down menu',
      linkType: 'display action link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'compare favorite shingles'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
    product: [
      {
        type: 'roofing shingle',
        name: `${shingleName} ${colorName}`.toLowerCase(),
        line: shingleName.toLowerCase(),
        detail: colorName.toLowerCase(),
      },
    ],
  };

  pushToAdobeDataLayer('trackFavoriteShingleSelection', eventData);
};

export const trackNewShingleSelection = (shingleName, clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'select',
      label: 'shingle comparison',
      detail: 'compare new shingle',
      type: 'drop-down menu',
      linkType: 'display action link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'compare new shingle'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
    product: [
      {
        type: 'roofing shingle',
        name: shingleName.toLowerCase(),
        line: shingleName.toLowerCase(),
        detail: shingleName.toLowerCase(),
      },
    ],
  };

  pushToAdobeDataLayer('trackNewShingleSelection', eventData);
};

export const trackReviewsLinkClick = (shingleName, colorName, clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'reviews',
      type: 'link',
      linkType: 'internal link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'reviews'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
    product: [
      {
        type: 'roofing shingle',
        name: `${shingleName} ${colorName}`.toLowerCase(),
        line: shingleName.toLowerCase(),
        detail: colorName.toLowerCase(),
      },
    ],
  };

  pushToAdobeDataLayer('trackReviewsLinkClick', eventData);
};

export const trackChangeColorLinkClick = (clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'change color',
      type: 'link',
      linkType: 'display action link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'change color'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
  };

  pushToAdobeDataLayer('trackChangeColorLinkClick', eventData);
};

export const trackChangeColorDropdownSelection = (shingleName, colorName, clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'select',
      label: 'shingle comparison',
      detail: 'change color',
      type: 'drop-down menu',
      linkType: 'display action link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'change color'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
    product: [
      {
        type: 'roofing shingle',
        name: `${shingleName} ${colorName}`.toLowerCase(),
        line: shingleName.toLowerCase(),
        detail: colorName.toLowerCase(),
      },
    ],
  };

  pushToAdobeDataLayer('trackChangeColorDropdownSelection', eventData);
};

export const trackCarouselPreviousArrowClick = (clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'previous',
      type: 'icon',
      linkType: 'display action link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'previous'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
  };

  pushToAdobeDataLayer('trackCarouselPreviousArrowClick', eventData);
};

export const trackCarouselNextArrowClick = (clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'next',
      type: 'icon',
      linkType: 'display action link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'next'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
  };

  pushToAdobeDataLayer('trackCarouselNextArrowClick', eventData);
};

export const trackCarouselDotClick = (clickInfo = {}) => {
  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'click',
      label: 'shingle comparison',
      detail: 'dot',
      type: 'icon',
      linkType: 'display action link',
      region: 'compare shingle colors and lines',
      regionType: 'page body',
      context: buildContext('compare shingle colors and lines', 'dot'),
      clickDetails: buildClickDetails(clickInfo.element, clickInfo.translatedClickText),
    },
  };

  pushToAdobeDataLayer('trackCarouselDotClick', eventData);
};

export const trackComparisonImpression = comparisonItems => {
  if (!comparisonItems || comparisonItems.length === 0) {
    return;
  }

  const eventData = {
    event: 'impression',
    eventInfo: {
      context: 'shingle_comparison_product_impression',
    },
    product: comparisonItems.map(item => ({
      type: 'roofing shingle',
      name: `${item.shingle.proper_name} ${item.shingleColor.name}`.toLowerCase(),
      line: item.shingle.proper_name.toLowerCase(),
      detail: item.shingleColor.name.toLowerCase(),
    })),
  };

  pushToAdobeDataLayer('trackComparisonImpression', eventData);
};

export const trackDrawerOpen = (clickInfo = {}) => {
  const clickDetails = buildClickDetails(clickInfo.element, clickInfo.translatedClickText);

  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'open drawer',
      label: 'shingle comparison',
      detail: clickDetails.clickText,
      type: 'drawer',
      linkType: 'display action link',
      region: 'shingle comparison drawer',
      regionType: 'drawer',
      clickDetails,
    },
  };

  pushToAdobeDataLayer('trackDrawerOpen', eventData);
};

export const trackDrawerClose = (clickInfo = {}) => {
  const clickDetails = buildClickDetails(clickInfo.element, clickInfo.translatedClickText);

  const eventData = {
    event: 'cta',
    eventInfo: {
      action: 'close drawer',
      label: 'shingle comparison',
      detail: clickDetails.clickText,
      type: 'drawer',
      linkType: 'display action link',
      region: 'shingle comparison drawer',
      regionType: 'drawer',
      clickDetails,
    },
  };

  pushToAdobeDataLayer('trackDrawerClose', eventData);
};
